import { useState } from "react";
import type { ReactNode } from "react";
import { Column } from "primereact/column";
import { ConfirmPopup } from "primereact/confirmpopup";
import { FloppyDiskIcon, XCircleIcon } from "@phosphor-icons/react";
import * as yup from "yup";

import type { BalanceUiT } from "../domain/ui/balance.model";
import type { AccrualRowUiT } from "../domain/ui/collaboratorAccruals.model";

import { bodyClassName, headerClassName } from "assets/customStyles";

import {
  ACCRUALS_TABLE_COLUMNS,
  ACCRUALS_TABLE_COLUMN_KEYS,
} from "../constants/accrualsTableColumns";
import { ACCRUALS_TABLE_STRINGS } from "../constants/strings";
import { buildAccrualRowSchema } from "../constants/accrualValidationSchema";
import { isoToDisplayDate } from "../helpers/accrualDateFormat";
import { GeneralNames } from "namesConstants/names";

import {
  ColaboraButton,
  ColaboraTable,
  InputText,
  TableActions,
  TableBodyAction,
} from "components/v2";
import { Toast } from "components/toast";

import { useCollaboratorAccrualsTable } from "../hooks/useCollaboratorAccrualsTable";
import { useQueryError } from "hooks/useQueryError";
import { useTableBehavior } from "hooks/useTableBehavior";
import { useRowEditing } from "shared/hooks/useRowEditing";

import useReload from "hooks/useReload";

import AccrualDateInput from "./AccrualDateInput";
import AccrualDecimalInput from "./AccrualDecimalInput";
import {
  NEGATIVE_ACCRUAL_TYPE,
  POSITIVE_ACCRUAL_TYPE,
} from "../constants/accrualType";

type AccrualsTablePropsT = {
  collaboratorBalance: BalanceUiT;
};

type RowErrorsT = Record<string, string>;

type SortMetaT = { field: string; order: number };

type RowEditorOptionsT = { rowData: AccrualRowUiT };

const ROW_EDITOR_INPUT_CLASSNAME =
  "!h-[1.25rem] !text-[14px] !py-[.85rem] !px-1";
const MAX_NEW_ACCRUAL_DRAFTS = 10;

const createBlankAccrualDraft = (): AccrualRowUiT => ({
  id: crypto.randomUUID(),
  isInitialBalance: false,
  registrationDate: "",
  registrationDateValue: null,
  description: "",
  vacationDays: 0,
  vacationTaken: 0,
  primaDays: 0,
  primaPercentage: 0,
});

const isEmptySortValue = (value: unknown) =>
  value === null || value === undefined || value === "";

const compareSortValues = (a: unknown, b: unknown, order: number): number => {
  const emptyA = isEmptySortValue(a);
  const emptyB = isEmptySortValue(b);

  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  if (typeof a === "string" && typeof b === "string") {
    return order * a.localeCompare(b, undefined, { numeric: true });
  }

  const result = a === b ? 0 : (a as number) < (b as number) ? -1 : 1;

  return order * result;
};

const sortAccruals = (
  rows: AccrualRowUiT[],
  multiSortMeta: SortMetaT[],
): AccrualRowUiT[] => {
  if (multiSortMeta.length === 0) return rows;

  return [...rows].sort((rowA, rowB) => {
    for (const meta of multiSortMeta) {
      const result = compareSortValues(
        (rowA as unknown as Record<string, unknown>)[meta.field],
        (rowB as unknown as Record<string, unknown>)[meta.field],
        meta.order,
      );

      if (result !== 0) return result;
    }

    return 0;
  });
};

const toFieldErrors = (validationError: unknown): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  if (validationError instanceof yup.ValidationError) {
    validationError.inner.forEach((innerError) => {
      if (innerError.path && !fieldErrors[innerError.path]) {
        fieldErrors[innerError.path] = innerError.message;
      }
    });
  }

  return fieldErrors;
};

const AccrualsTable = ({ collaboratorBalance }: AccrualsTablePropsT) => {
  const {
    data,
    isLoading,
    refetch,
    isError,
    error,
    deleteAccruals: { mutateAsync: deleteAccrual, isPending: isDeleting },
    updateAccruals: { mutateAsync: updateAccrual, isPending: isUpdating },
    createAccrual: { mutateAsync: createAccrual, isPending: isCreating },
  } = useCollaboratorAccrualsTable({
    collaboratorId: collaboratorBalance.id,
  });

  const { sort, setSort, searchTextFilter } = useTableBehavior({
    initialSort: [
      { field: ACCRUALS_TABLE_COLUMN_KEYS.REGISTRATION_DATE_VALUE, order: -1 },
    ],
  });

  const [editDraft, setEditDraft] = useState<AccrualRowUiT | null>(null);
  const [rowErrors, setRowErrors] = useState<RowErrorsT>({});
  const [newDrafts, setNewDrafts] = useState<AccrualRowUiT[]>([]);
  const [newDraftErrors, setNewDraftErrors] = useState<
    Record<string, RowErrorsT>
  >({});

  const rowEditing = useRowEditing<AccrualRowUiT>({
    onCancel: (row) => {
      setNewDrafts((prev) => prev.filter((draft) => draft.id !== row.id));
      setNewDraftErrors((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });

      if (editDraft?.id === row.id) {
        setEditDraft(null);
        setRowErrors({});
      }
    },
  });

  useReload([refetch]);
  useQueryError({
    isError,
    error,
    message: ACCRUALS_TABLE_STRINGS.GET_DATA_ERROR_TITLE,
  });

  const getRowDraft = (rowId: string): AccrualRowUiT | undefined => {
    if (editDraft?.id === rowId) return editDraft;

    return newDrafts.find((draft) => draft.id === rowId);
  };

  const getRowErrors = (rowId: string): RowErrorsT => {
    if (editDraft?.id === rowId) return rowErrors;

    return newDraftErrors[rowId] ?? {};
  };

  const setRowErrorsFor = (rowId: string, errors: RowErrorsT) => {
    if (editDraft?.id === rowId) {
      setRowErrors(errors);
      return;
    }

    setNewDraftErrors((prev) => ({ ...prev, [rowId]: errors }));
  };

  const validateDraft = (rowId: string, draft: AccrualRowUiT): boolean => {
    try {
      buildAccrualRowSchema(draft.accrualType).validateSync(draft, {
        abortEarly: false,
      });

      setRowErrorsFor(rowId, {});

      return true;
    } catch (validationError) {
      const nextErrors = toFieldErrors(validationError);
      const previousErrors = getRowErrors(rowId);

      Object.entries(nextErrors).forEach(([field, message]) => {
        if (previousErrors[field] !== message) {
          Toast("warning", message);
        }
      });

      setRowErrorsFor(rowId, nextErrors);

      return false;
    }
  };

  const onEditAccrual = (row: AccrualRowUiT) => {
    setEditDraft({ ...row });
    setRowErrors({});

    rowEditing.startEditing(row);
  };

  const onAddAccrual = () => {
    if (newDrafts.length >= MAX_NEW_ACCRUAL_DRAFTS) return;

    const draft = createBlankAccrualDraft();

    setNewDrafts((prev) => [...prev, draft]);
    rowEditing.startEditing(draft);
  };

  const updateDraftField = <K extends keyof AccrualRowUiT>(
    rowId: string,
    field: K,
    value: AccrualRowUiT[K],
  ) => {
    if (editDraft?.id === rowId) {
      const next = { ...editDraft, [field]: value };

      setEditDraft(next);
      validateDraft(rowId, next);
      return;
    }

    setNewDrafts((prev) =>
      prev.map((draft) => {
        if (draft.id !== rowId) return draft;

        const next = { ...draft, [field]: value };

        validateDraft(rowId, next);

        return next;
      }),
    );
  };

  const onDeleteAccrual = async (accrualId: string) => {
    try {
      const result = await deleteAccrual({ accrualIds: [accrualId] });

      if (result.deleted > 0) {
        Toast("success", ACCRUALS_TABLE_STRINGS.DELETE_SUCCESS);
      }

      if (result.errors.length > 0) {
        Toast(
          "warning",
          ACCRUALS_TABLE_STRINGS.DELETE_PARTIAL_FAILURE,
          result.errors[0].error,
        );
      }
    } catch (error) {
      Toast(
        "error",
        ACCRUALS_TABLE_STRINGS.DELETE_ERROR,
        (error as Error).message,
      );
    }
  };

  const onSaveNewAccrual = async (draft: AccrualRowUiT) => {
    try {
      const result = await createAccrual({
        rows: [
          {
            collaboratorId: collaboratorBalance.id,
            description: draft.description,
            isInitialBalance: draft.isInitialBalance,
            admissionDate: draft.registrationDate,
            vacationDaysAccrued: draft.vacationDays ?? 0,
            vacationDaysTaken: draft.vacationTaken ?? 0,
            vacationPremiumPct: draft.primaPercentage ?? 0,
            vacationPremiumDays: draft.primaDays ?? 0,
          },
        ],
      });

      if (result.errors.length > 0) {
        Toast(
          "warning",
          ACCRUALS_TABLE_STRINGS.CREATE_PARTIAL_FAILURE,
          result.errors[0].error,
        );
        return;
      }

      if (result.processed > 0) {
        Toast("success", ACCRUALS_TABLE_STRINGS.CREATE_SUCCESS);
      }

      setNewDrafts((prev) => prev.filter((item) => item.id !== draft.id));
      setNewDraftErrors((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
      rowEditing.saveRow(draft);
    } catch (error) {
      Toast(
        "error",
        ACCRUALS_TABLE_STRINGS.CREATE_ERROR,
        (error as Error).message,
      );
    }
  };

  const onSaveExistingAccrual = async (draft: AccrualRowUiT) => {
    try {
      const isNegativeAccrual = draft.accrualType === NEGATIVE_ACCRUAL_TYPE;

      const result = await updateAccrual({
        accruals: [
          {
            id: draft.id,
            description: draft.description,
            isInitialBalance: draft.isInitialBalance,
            primaDays: draft.primaDays ?? 0,
            primaPercentage: draft.primaPercentage ?? 0,
            registrationDate: draft.registrationDate,
            vacationDays: isNegativeAccrual
              ? draft.vacationTaken
              : (draft.vacationDays ?? 0),
          },
        ],
      });

      if (result.errors.length > 0) {
        Toast(
          "warning",
          ACCRUALS_TABLE_STRINGS.UPDATE_PARTIAL_FAILURE,
          result.errors[0].error,
        );
        return;
      }

      if (result.updated > 0) {
        Toast("success", ACCRUALS_TABLE_STRINGS.UPDATE_SUCCESS);
      }

      setRowErrors({});
      rowEditing.saveRow(draft);
      setEditDraft(null);
    } catch (error) {
      Toast(
        "error",
        ACCRUALS_TABLE_STRINGS.UPDATE_ERROR,
        (error as Error).message,
      );
    }
  };

  const onSaveRow = async (rowId: string) => {
    const draft = getRowDraft(rowId);

    if (!draft) return;

    if (!validateDraft(rowId, draft)) return;

    const isNewDraft = newDrafts.some((item) => item.id === rowId);

    if (isNewDraft) {
      await onSaveNewAccrual(draft);
      return;
    }

    await onSaveExistingAccrual(draft);
  };

  const registrationDateEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    return (
      <AccrualDateInput
        id={`registration-date-${draft.id}`}
        value={draft.registrationDate}
        onChange={(e) =>
          updateDraftField(draft.id, "registrationDate", e.target.value)
        }
        error={getRowErrors(draft.id).registrationDate}
        inputClassName={ROW_EDITOR_INPUT_CLASSNAME}
      />
    );
  };

  const descriptionEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    const invalidClassName = getRowErrors(draft.id).description
      ? " p-invalid"
      : "";

    return (
      <InputText
        id={`description-${draft.id}`}
        value={draft.description}
        onChange={(e) =>
          updateDraftField(draft.id, "description", e.target.value)
        }
        inputClassName={`${ROW_EDITOR_INPUT_CLASSNAME}${invalidClassName}`}
      />
    );
  };

  const vacationDaysEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    return (
      <AccrualDecimalInput
        id={`vacation-days-${draft.id}`}
        value={draft.vacationDays}
        onChange={(e) =>
          updateDraftField(draft.id, "vacationDays", e.target.value)
        }
        disabled={draft.accrualType === NEGATIVE_ACCRUAL_TYPE}
        error={getRowErrors(draft.id).vacationDays}
        inputClassName={ROW_EDITOR_INPUT_CLASSNAME}
      />
    );
  };

  const takenDaysEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    return (
      <AccrualDecimalInput
        id={`taken-days-${draft.id}`}
        value={draft.vacationTaken}
        onChange={(e) =>
          updateDraftField(draft.id, "vacationTaken", e.target.value)
        }
        disabled={draft.accrualType === POSITIVE_ACCRUAL_TYPE}
        error={getRowErrors(draft.id).vacationTaken}
        inputClassName={ROW_EDITOR_INPUT_CLASSNAME}
      />
    );
  };

  const primaPercentageEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    return (
      <AccrualDecimalInput
        id={`prima-percentage-${draft.id}`}
        value={draft.primaPercentage}
        onChange={(e) =>
          updateDraftField(draft.id, "primaPercentage", e.target.value)
        }
        error={getRowErrors(draft.id).primaPercentage}
        inputClassName={ROW_EDITOR_INPUT_CLASSNAME}
      />
    );
  };

  const primaDaysEditor = ({ rowData }: RowEditorOptionsT) => {
    const draft = getRowDraft(rowData.id);

    if (!draft) return null;

    return (
      <AccrualDecimalInput
        id={`prima-days-${draft.id}`}
        value={draft.primaDays}
        onChange={(e) =>
          updateDraftField(draft.id, "primaDays", e.target.value)
        }
        error={getRowErrors(draft.id).primaDays}
        inputClassName={ROW_EDITOR_INPUT_CLASSNAME}
      />
    );
  };

  const COLUMN_EDITORS: Record<
    string,
    (options: RowEditorOptionsT) => ReactNode
  > = {
    [ACCRUALS_TABLE_COLUMN_KEYS.REGISTRATION_DATE]: registrationDateEditor,
    [ACCRUALS_TABLE_COLUMN_KEYS.DESCRIPTION]: descriptionEditor,
    [ACCRUALS_TABLE_COLUMN_KEYS.VACATION_DAYS]: vacationDaysEditor,
    [ACCRUALS_TABLE_COLUMN_KEYS.TAKEN_DAYS]: takenDaysEditor,
    [ACCRUALS_TABLE_COLUMN_KEYS.PRIMA_PERCENTAGE]: primaPercentageEditor,
    [ACCRUALS_TABLE_COLUMN_KEYS.PRIMA_DAYS]: primaDaysEditor,
  };

  const actionsBodyTemplate = (rowData: AccrualRowUiT) => {
    if (rowEditing.isRowEditing(rowData)) {
      return (
        <fieldset
          aria-label="Acciones de la fila"
          className="flex justify-center content-center items-center gap-2 border-none p-0 m-0"
        >
          <TableBodyAction
            icon={<FloppyDiskIcon size={30} />}
            toolTip={ACCRUALS_TABLE_STRINGS.TEXT_TOOLTIP_SAVE}
            disabled={isUpdating || isCreating}
            action={() => onSaveRow(rowData.id)}
          />
          <TableBodyAction
            icon={<XCircleIcon size={30} />}
            toolTip={ACCRUALS_TABLE_STRINGS.TEXT_TOOLTIP_CANCEL}
            action={() => rowEditing.cancelRow(rowData)}
          />
        </fieldset>
      );
    }

    const isAnyRowEditing = editDraft !== null || newDrafts.length > 0;

    return (
      <TableActions
        rowData={rowData}
        onEditClicked={() => onEditAccrual(rowData)}
        onDeleteConfirm={onDeleteAccrual}
        editTooltip={ACCRUALS_TABLE_STRINGS.TEXT_TOOLTIP_EDIT}
        deleteTooltip={ACCRUALS_TABLE_STRINGS.TEXT_TOOLTIP_DELETE}
        deleteConfirmMessage={ACCRUALS_TABLE_STRINGS.DELETE_CONFIRM_MESSAGE}
        editDisabled={isAnyRowEditing}
        deleteDisabled={isDeleting || isAnyRowEditing}
      />
    );
  };

  const hasPendingDrafts = newDrafts.length > 0;
  const tableData = [
    ...sortAccruals(data?.accruals ?? [], sort as SortMetaT[]),
    ...newDrafts,
  ];

  return (
    <>
      <ConfirmPopup />
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-400 font-normal">
            {collaboratorBalance.fullName}
          </span>
          {data?.vacationSourceStart && data?.accruals.length > 0 && (
            <span className="text-sm text-gray-400 font-normal">
              {`Fecha de carga de saldo inicial: ${isoToDisplayDate(data.vacationSourceStart)}`}
            </span>
          )}
        </div>
        <ColaboraButton
          label={ACCRUALS_TABLE_STRINGS.BUTTON_ADD_ACCRUAL}
          onClick={onAddAccrual}
          disabled={newDrafts.length >= MAX_NEW_ACCRUAL_DRAFTS}
          isLoading={isCreating}
          className="!w-[12rem]"
        />
      </div>
      <ColaboraTable
        data={tableData}
        isLoadingData={isLoading}
        sort={hasPendingDrafts ? [] : sort}
        onChangeSort={setSort}
        filters={searchTextFilter}
        emptyMessage={GeneralNames.GeneralEmptyMessage}
        currentPageReportTemplate={
          isLoading
            ? ACCRUALS_TABLE_STRINGS.TEXT_TABLE_LOADING
            : ACCRUALS_TABLE_STRINGS.TEXT_TABLE_RESULTS
        }
        paginator={false}
        editMode="row"
        dataKey="id"
        editingRows={rowEditing.editingRows}
        onRowEditChange={rowEditing.onRowEditChange}
      >
        {ACCRUALS_TABLE_COLUMNS.map((col) => (
          <Column
            key={`${col.field}_accruals`}
            headerClassName={headerClassName}
            style={col.style}
            field={col.field}
            header={col.header}
            body={col.body}
            editor={COLUMN_EDITORS[col.field]}
            bodyClassName={bodyClassName}
            align={col.align}
            sortable={col.sortable && !hasPendingDrafts}
            sortField={col.sortField}
          />
        ))}
        <Column
          headerClassName={headerClassName}
          style={{ minWidth: "8rem", width: "8rem" }}
          body={actionsBodyTemplate}
          bodyClassName={bodyClassName}
          align="center"
        />
      </ColaboraTable>
    </>
  );
};

export default AccrualsTable;
